import { hasDeviceExternalUrl } from "../deviceConnectionUtils";
import { keyboardVidPidNeedsProductNameSplit } from "../keyboardDeviceMatch";
import { isGenericHidKeyboardProductName } from "../genericKeyboardOtherOnly";

const normProductName = (s: unknown) =>
  typeof s === "string" ? s.trim().toLowerCase() : "";

/** 配置表 usagePage/usage 是否为有效值（0 表示尚未实机探测） */
export function isSdcxUsageConfigured(configuration: any): boolean {
  const usagePage = configuration?.usagePage;
  const usage = configuration?.usage;
  return (
    typeof usagePage === "number" &&
    typeof usage === "number" &&
    usagePage !== 0 &&
    usage !== 0
  );
}

/** 是否为 SDCX 驱动内键盘方案 */
export function isSdcxKeyboard(configuration: any): boolean {
  if (!configuration || configuration.solutionRovider !== "SDCX") {
    return false;
  }
  if (hasDeviceExternalUrl(configuration)) return false;
  return true;
}

function collectionEquals(
  collection: any,
  usagePage: number,
  usage: number
): boolean {
  return collection?.usagePage === usagePage && collection?.usage === usage;
}

/** SDCX HID collection 是否与配置表 usagePage/usage 一致 */
export function sdcxHidCollectionMatches(
  configuration: any,
  collection: any
): boolean {
  if (!isSdcxKeyboard(configuration)) {
    return false;
  }
  if (!isSdcxUsageConfigured(configuration)) {
    return false;
  }
  return collectionEquals(
    collection,
    configuration.usagePage,
    configuration.usage
  );
}

/** 从 HID collections 中探测厂商自定义通信接口（实机后回填配置表用） */
export function probeSdcxHidUsage(
  device: any
): { usagePage: number; usage: number } | null {
  const collections = device?.collections ?? [];
  for (const collection of collections) {
    if (
      typeof collection?.usagePage === "number" &&
      collection.usagePage >= 0xff00
    ) {
      return {
        usagePage: collection.usagePage,
        usage: typeof collection.usage === "number" ? collection.usage : 0,
      };
    }
  }
  return null;
}

export type SdcxDeviceInfoPayload = {
  protocolVersion: number;
  deviceId: number;
  firmwareVersion: number;
  workMode: number;
  connectionStatus: number;
  battery: number;
  charging: number;
  profileCount: number;
  currentProfile: number;
  layerCount: number;
  currentLayer: number;
  raw: Uint8Array;
};

/** 解析 CMD 0x15 设备基本信息 payload */
export function parseSdcxDeviceInfoPayload(
  payload: Uint8Array
): SdcxDeviceInfoPayload {
  return {
    protocolVersion: (payload[0] ?? 0) | ((payload[1] ?? 0) << 8),
    deviceId: (payload[2] ?? 0) | ((payload[3] ?? 0) << 8),
    firmwareVersion: (payload[4] ?? 0) | ((payload[5] ?? 0) << 8),
    workMode: payload[6] ?? 0,
    connectionStatus: payload[7] ?? 0,
    battery: payload[8] ?? 0,
    charging: payload[9] ?? 0,
    profileCount: payload[10] ?? 0,
    currentProfile: payload[11] ?? 0,
    layerCount: payload[12] ?? 0,
    currentLayer: payload[13] ?? 0,
    raw: payload.slice(0, 14),
  };
}

/**
 * 固件版本展示（CMD 0x05 byte4~5，小端 u16）
 * 协议示例 100 → V1.0；实机常见 5 → V0.05
 */
export function formatSdcxFirmwareVersion(version: number): string {
  const raw = Math.max(0, Math.trunc(Number(version) || 0));
  if (raw === 0) {
    return "1.0";
  }
  if (raw >= 100) {
    const major = Math.floor(raw / 100);
    const minor = raw % 100;
    return minor === 0 ? `${major}.0` : `${major}.${minor}`;
  }
  return raw < 10 ? `0.0${raw}` : `0.${raw}`;
}

/** SDCX HID 是否与配置表行对应 */
export function sdcxHidMatchesConfiguration(
  configuration: any,
  device: any
): boolean {
  if (!isSdcxKeyboard(configuration)) {
    return false;
  }
  if (
    device?.vendorId !== configuration.vendorId ||
    device?.productId !== configuration.productId
  ) {
    return false;
  }
  const collections = device?.collections ?? [];
  let hasMatchingCollection = collections.some((collection: any) =>
    sdcxHidCollectionMatches(configuration, collection)
  );
  // usage 未配置时，回退到任意厂商自定义接口（0xFF00+）
  if (!hasMatchingCollection) {
    hasMatchingCollection = collections.some(
      (collection: any) =>
        typeof collection?.usagePage === "number" &&
        collection.usagePage >= 0xff00
    );
  }
  if (!hasMatchingCollection) {
    return false;
  }
  if (isGenericHidKeyboardProductName(device?.productName)) {
    return true;
  }
  const cfgPn = normProductName(configuration.productName);
  const devPn = normProductName(device?.productName);
  if (
    keyboardVidPidNeedsProductNameSplit(
      configuration.vendorId,
      configuration.productId
    )
  ) {
    return cfgPn !== "" && cfgPn === devPn;
  }
  return true;
}
